import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import PublicOracleWall from "@/components/PublicOracleWall";

const SECTION_LABELS = {
  es: {
    radar: "RADAR ENERGÉTICO",
    diagnosis: "DIAGNÓSTICO ENERGÉTICO",
    shadow: "SOMBRA ACTIVA",
    virtue: "VIRTUD A ACTIVAR",
    wellbeing: "SALUD Y BIENESTAR",
    money: "DINERO",
    work: "TRABAJO Y PROYECTOS",
    emotional: "EMOCIONAL",
    love: "AMOR Y RELACIONES",
    tisane: "TISANA DEL DÍA",
    frequency: "FRECUENCIA ENERGÉTICA",
    message: "MENSAJE DEL DÍA",
    energy_number: "NÚMERO ENERGÉTICO",
    activation: "ACTIVACIÓN",
    closing: "CLAVE DEL DÍA",
  },
  en: {
    radar: "ENERGY RADAR",
    diagnosis: "ENERGY DIAGNOSIS",
    shadow: "ACTIVE SHADOW",
    virtue: "VIRTUE TO ACTIVATE",
    wellbeing: "HEALTH & WELLBEING",
    money: "MONEY",
    work: "WORK & PROJECTS",
    emotional: "EMOTIONAL",
    love: "LOVE & RELATIONSHIPS",
    tisane: "TISANE OF THE DAY",
    frequency: "ENERGY FREQUENCY",
    message: "MESSAGE OF THE DAY",
    energy_number: "ENERGY NUMBER",
    activation: "ACTIVATION",
    closing: "KEY OF THE DAY",
  },
};

const DETAIL_FIELDS = ["diagnosis", "shadow", "virtue", "wellbeing", "money", "work", "emotional", "love", "tisane", "frequency"];

const DailyHoroscope = ({ embedded = false, inDashboard = false, showOracleWall = true }) => {
  const { lang } = useLanguage();
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState("");
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let mounted = true;
    setError(false);
    api.get("/api/landing-vivo/horoscope")
      .then(({ data: response }) => {
        if (!mounted) return;
        const signs = Array.isArray(response?.signs) ? response.signs : [];
        setData({ ...response, signs });
        const firstAvailable = signs.find((sign) => sign.available);
        setSelected((firstAvailable || signs[0] || {}).key || "");
      })
      .catch(() => mounted && setError(true));
    return () => { mounted = false; };
  }, [attempt]);

  const locale = data?.content?.[lang] || data?.content?.es;
  const sign = useMemo(
    () => data?.signs?.find((item) => item.key === selected),
    [data, selected],
  );
  const horoscope = sign?.horoscope?.[lang] || sign?.horoscope?.es;

  if (error || !data || !data.enabled || !data.signs.length) {
    return (
      <>
        {embedded && <div className="journey-horoscope-status" role="status">
          {error ? "No pudimos consultar la energía diaria. Puedes intentarlo de nuevo." : !data ? "Consultando la energía del día…" : "La orientación del día aún no está publicada. Vuelve a explorar el territorio y consúltala más tarde."}
          {error && <button onClick={() => setAttempt((value) => value + 1)}>Reintentar</button>}
        </div>}
        {showOracleWall && <PublicOracleWall embedded={embedded} />}
      </>
    );
  }

  return (
    <>
    <section className={inDashboard ? "relative w-full min-w-0 pt-3 pb-8" : "relative max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-4"} data-testid="landing-vivo-horoscope">
      <div className={inDashboard ? "kd9-card p-3 sm:p-5 md:p-8 overflow-hidden" : "kd9-card p-5 md:p-8 overflow-hidden"}>
        <div className="text-center mb-5">
          <div className="font-mont text-[10px] tracking-[0.4em] uppercase mb-2" style={{ color: "var(--kd9-accent)" }}>
            · {locale?.kicker} ·
          </div>
          <h2 className="font-cinzel kd9-gold-text text-2xl md:text-3xl tracking-[0.12em]">
            {locale?.title}
          </h2>
          {locale?.subtitle && <p className="font-cormorant text-lg mt-2" style={{ color: "var(--kd9-text-soft)" }}>{locale.subtitle}</p>}
        </div>

        <div className={inDashboard ? "grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2 mb-6" : "flex flex-wrap justify-center gap-2 mb-6"} role="tablist" aria-label={locale?.title}>
          {data.signs.map((item) => (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={selected === item.key}
              onClick={() => setSelected(item.key)}
              className={selected === item.key ? (inDashboard ? "kd9-btn-gold min-w-0 px-2 py-3 rounded-lg text-[10px] leading-tight" : "kd9-btn-gold px-3 py-2 rounded-lg text-[10px]") : (inDashboard ? "kd9-btn-outline min-w-0 px-2 py-3 rounded-lg text-[10px] leading-tight" : "kd9-btn-outline px-3 py-2 rounded-lg text-[10px]")}
              data-testid={`horoscope-sign-${item.key}`}
            >
              <span className="text-base mr-1" aria-hidden="true">{item.icon}</span>
              {lang === "en" ? item.label_en : item.label_es}
            </button>
          ))}
        </div>

        {sign && (
          <article className={inDashboard ? "max-w-5xl mx-auto rounded-2xl p-3 sm:p-5 md:p-7" : "max-w-5xl mx-auto rounded-2xl p-5 md:p-7"} style={{ background: "var(--kd9-bg-2)", border: "1px solid var(--kd9-border)" }}>
            <div className="text-center">
              <div className="text-3xl mb-2" aria-hidden="true">{sign.icon}</div>
              <h3 className="font-cinzel kd9-gold-text text-xl mb-3">
                {lang === "en" ? sign.label_en : sign.label_es}
                {horoscope?.headline ? ` · ${horoscope.headline}` : ""}
              </h3>
            </div>

            {horoscope?.radar && (
              <div className="rounded-xl border p-4 mb-4" style={{ borderColor: "rgba(212,175,55,.28)", background: "rgba(212,175,55,.05)" }}>
                <div className="font-mont text-[9px] tracking-[.2em] kd9-gold-text mb-2">{SECTION_LABELS[lang]?.radar || SECTION_LABELS.es.radar}</div>
                <p className="font-cormorant text-lg leading-relaxed">{horoscope.radar}</p>
              </div>
            )}

            {horoscope?.message || horoscope?.diagnosis ? (
              <>
                <div className="grid md:grid-cols-2 gap-3">
                  {DETAIL_FIELDS.filter((field) => horoscope?.[field]).map((field) => (
                    <div key={field} className="rounded-xl border p-4" style={{ borderColor: "var(--kd9-border)", background: "rgba(0,0,0,.12)" }}>
                      <div className="font-mont text-[9px] tracking-[.16em] uppercase mb-2" style={{ color: "var(--kd9-accent)" }}>
                        {SECTION_LABELS[lang]?.[field] || SECTION_LABELS.es[field]}
                      </div>
                      <p className="font-cormorant text-base sm:text-lg leading-relaxed">{horoscope[field]}</p>
                    </div>
                  ))}
                </div>

                {horoscope.message && (
                  <div className="rounded-xl border p-4 mt-4 text-center" style={{ borderColor: "rgba(212,175,55,.32)" }}>
                    <div className="font-mont text-[9px] tracking-[.2em] kd9-gold-text mb-2">{SECTION_LABELS[lang]?.message || SECTION_LABELS.es.message}</div>
                    <p className="font-cormorant text-xl leading-relaxed">{horoscope.message}</p>
                  </div>
                )}

                {(horoscope.energy_number || horoscope.activation) && (
                  <div className="grid sm:grid-cols-[180px_1fr] gap-3 mt-4">
                    {horoscope.energy_number && <div className="rounded-xl border p-4 text-center" style={{ borderColor: "var(--kd9-border)" }}>
                      <div className="font-mont text-[9px] tracking-[.16em] mb-2" style={{ color: "var(--kd9-text-soft)" }}>{SECTION_LABELS[lang]?.energy_number || SECTION_LABELS.es.energy_number}</div>
                      <div className="font-cinzel kd9-gold-text text-3xl tracking-[.2em]">{horoscope.energy_number}</div>
                    </div>}
                    {horoscope.activation && <div className="rounded-xl border p-4" style={{ borderColor: "var(--kd9-border)" }}>
                      <div className="font-mont text-[9px] tracking-[.16em] mb-2" style={{ color: "var(--kd9-text-soft)" }}>{SECTION_LABELS[lang]?.activation || SECTION_LABELS.es.activation}</div>
                      <p className="font-cormorant text-lg leading-relaxed">{horoscope.activation}</p>
                    </div>}
                  </div>
                )}

                {horoscope.closing && <div className="mt-4 text-center">
                  <div className="font-mont text-[9px] tracking-[.16em] mb-2" style={{ color: "var(--kd9-text-soft)" }}>{SECTION_LABELS[lang]?.closing || SECTION_LABELS.es.closing}</div>
                  <p className="font-cormorant text-lg leading-relaxed">{horoscope.closing}</p>
                </div>}

                {horoscope.guidance && <p className="font-mont text-[10px] mt-5 text-center tracking-wide" style={{ color: "var(--kd9-text-soft)" }}>{horoscope.guidance}</p>}
              </>
            ) : (
              <p className="font-cormorant text-lg text-center" style={{ color: "var(--kd9-text-soft)" }}>{locale?.empty_message}</p>
            )}
          </article>
        )}
      </div>
    </section>
    {showOracleWall && <PublicOracleWall embedded={embedded} />}
    </>
  );
};

export default DailyHoroscope;
